import { useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useUIBuilderStore } from '../../stores/uiBuilderStore';
import { useAuthStore } from '../../stores/authStore';
import { useI18n } from '../../providers/I18nProvider';
import { UIComponent, AccessControl } from '../ui-builder/types';
import ComponentRenderer from '../ui-builder/components/ComponentRenderer';
import { executeScriptWithERP } from '../ui-builder/scriptRuntime';

/**
 * Check if the current user has access based on an AccessControl config.
 * Returns true if access is granted.
 */
function checkAccess(
  access: AccessControl | undefined,
  companyRole: string | null,
  permissionCodes: string[],
  groupCodes: string[],
  isGlobalSuperAdmin: boolean,
): boolean {
  if (!access || access.mode === 'public') return true;
  if (isGlobalSuperAdmin) return true;

  const hasRole =
    !access.requiredRoles?.length ||
    access.requiredRoles.some((r) => r === companyRole);

  const hasPerm =
    !access.requiredPermissions?.length ||
    access.requiredPermissions.some((p) => permissionCodes.includes(p));

  const hasGroup =
    !access.requiredGroups?.length ||
    access.requiredGroups.some((g) => groupCodes.includes(g));

  // Collect only the access checks that are explicitly configured.
  const checks: boolean[] = [];
  if (access.requiredRoles?.length) checks.push(hasRole);
  if (access.requiredPermissions?.length) checks.push(hasPerm);
  if (access.requiredGroups?.length) checks.push(hasGroup);

  // Access uses OR semantics across configured criteria: if roles,
  // permissions, and/or groups are configured, the user only needs to
  // satisfy at least one configured criterion to gain access. Unconfigured
  // criteria are ignored and do not make the access check stricter.
  if (checks.length === 0) return true;
  return checks.some(Boolean);
}

export default function CustomPageDisplay() {
  const { slug } = useParams<{ slug: string }>();
  const { t } = useI18n();
  const pages = useUIBuilderStore((state) => state.pages);
  const companyRole = useAuthStore((state) => state.companyRole);
  const permissionCodes = useAuthStore((state) => state.permissionCodes);
  const groupCodes = useAuthStore((state) => state.groupCodes);
  const isGlobalSuperAdmin = useAuthStore((state) => state.isGlobalSuperAdmin);

  const page = pages.find((p) => p.slug === slug);

  const handleButtonClick = useCallback((component: UIComponent, event: React.MouseEvent) => {
    const script = component.script;
    if (!script) return;

    const mockConsole = {
      log: (...args: unknown[]) => console.log('[CustomPage Script]', ...args),
      warn: (...args: unknown[]) => console.warn('[CustomPage Script]', ...args),
      error: (...args: unknown[]) => console.error('[CustomPage Script]', ...args),
    };

    executeScriptWithERP(script, component, event.nativeEvent, mockConsole, {
      allowDOM: true,
      allowFetch: true,
    }).catch((err) => {
      console.error('[CustomPage Script Error]', err);
    });
  }, []);

  if (!page) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">{t('customPage.notFound')}</h2>
          <p className="text-gray-600 dark:text-gray-400">
            {t('customPage.notFoundDescription')}
          </p>
        </div>
      </div>
    );
  }

  // Page-level access check
  if (!checkAccess(page.access, companyRole, permissionCodes, groupCodes, isGlobalSuperAdmin)) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">{t('common.accessDenied') || 'Access Denied'}</h2>
          <p className="text-gray-600 dark:text-gray-400">
            {t('common.accessDeniedDescription') || 'You do not have permission to view this page.'}
          </p>
        </div>
      </div>
    );
  }

  const renderComponent = (component: UIComponent) => {
    const hasAccess = checkAccess(component.access, companyRole, permissionCodes, groupCodes, isGlobalSuperAdmin);

    if (!hasAccess) {
      // Hidden mode: don't render at all
      if (component.access?.hideWhenRestricted !== false) {
        return null;
      }
      // Disabled mode: render with disabled overlay
      return (
        <div key={component.id} className="mb-4 pointer-events-none opacity-40">
          <ComponentRenderer component={component} />
        </div>
      );
    }

    return (
      <div key={component.id} className="mb-4">
        <ComponentRenderer component={component} onButtonClick={handleButtonClick} isPreview={true} />
      </div>
    );
  };

  return (
    <div className="flex flex-col h-full">
      <div className="mb-6">
        <h1 className="text-2xl font-bold">{page.name}</h1>
        {page.description && (
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            {page.description}
          </p>
        )}
      </div>

      <div className="flex-1 overflow-auto card p-6">
        {page.components.map((component: UIComponent) => renderComponent(component))}
      </div>
    </div>
  );
}